import React from "react";
import styled from "styled-components";
import Cloud from "@charty/cloud";
import Word from "@charty/cloud/word";
import { useAuthorWidgetData } from "@radarly/core/widgets/use-cases";
import { useCloudAuthorsWidget } from "@radarly/core/widgets/use-cases";
import { DatavizCloud } from "@radarly/models/dataviz";
import { SearchableInfluencer } from "@radarly/models/influencer";
import { SearchFragmentUsers } from "@radarly/models/search";
import Empty from "../../empty";
import Loading from "../../loading";
import Tooltip from "../../tooltip/metric";

interface SharedWidgetsAuthorsCloudProps {
  project: number;
  fragment: SearchFragmentUsers;
  dataviz: DatavizCloud;
  onClickInfluencer?: (influencer: SearchableInfluencer) => void;
  className?: string;
  forceReload?: boolean;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  closeFullscreen?: () => void;
}

type InnerProps = Omit<
  SharedWidgetsAuthorsCloudProps,
  "project" | "forceReload"
> & {
  data: SearchFragmentUsers["data"];
};

const Container = styled.div`
  height: 100%;
`;

function Inner({
  data,
  fragment,
  dataviz,
  className,
  onClickInfluencer,
  disableAnimation,
  isFullscreen,
  closeFullscreen,
}: InnerProps) {
  const { metric, locale, range, authors, onClickAuthor, limit, order } =
    useCloudAuthorsWidget(data, fragment, onClickInfluencer);

  const _onClickAuthor = (uid: string | number) => {
    if (isFullscreen) {
      closeFullscreen();
    }
    onClickAuthor(uid);
  };

  return (
    <Container>
      <Cloud
        className={className}
        items={authors}
        color={dataviz.color}
        order={order}
        limit={limit}
        disableAnimation={disableAnimation}
        renderItem={(item, animated) => (
          <Word
            onClick={onClickAuthor ? () => _onClickAuthor(item.id) : null}
            label={item.label}
            normalizedWeight={item.normalizedWeight}
            locale={locale}
            isHidden={item.isHidden}
            weight={item.weight}
            animated={animated}
            color={item.color}
            tooltip={(serie) => (
              <Tooltip
                range={range}
                count={serie.weight}
                label={serie.label}
                metric={metric}
                locale={locale}
              />
            )}
          />
        )}
      />
    </Container>
  );
}

export default function SharedWidgetsAuthorsCloud({
  project,
  fragment,
  forceReload,
  ...props
}: SharedWidgetsAuthorsCloudProps) {
  const { loading, data, isEmpty } = useAuthorWidgetData(project, fragment);
  return (
    <Loading loading={loading} forceReload={forceReload}>
      <Empty empty={isEmpty}>
        <Inner fragment={fragment} data={data} {...props} />
      </Empty>
    </Loading>
  );
}
